import { DateTime } from 'luxon';
import { logWhenReady } from '@/app/analytics';
import { SignupRequestError } from './signup-api/signup-api';

/** The reads signup recovers from by carrying on along the route it would take anyway. */
export type SignupFallbackRead =
  | 'registration_profile_after_code'
  | 'sponsor_after_search'
  | 'sponsor_before_save'
  | 'signup_resume_read'
  | 'registration_profile_on_welcome_back';

/** The route a read that failed falls back to. */
export type SignupReadFallback =
  | 'continue_signup'
  | 'skip_plan_details'
  | 'save_without_plan_details'
  | 'plain_welcome'
  | 'show_error_body';

/**
 * A fixed classification of why a read failed: the transport kind and the
 * server's machine code when it gave one, never its message.
 */
function failureOf(error: unknown): string {
  if (!(error instanceof SignupRequestError)) return 'unexpected';
  return [error.kind ?? 'request', error.code].filter(Boolean).join(':');
}

/**
 * Runs `read` and logs it once, success or failure, so a failure the member
 * never sees stays countable against every attempt. A failure resolves to
 * null for the caller to take `fallback`. Nothing the read returned, and no
 * error text, leaves the browser.
 */
export async function readWithFallback<T>(
  operation: SignupFallbackRead,
  fallback: SignupReadFallback,
  read: () => Promise<T>,
): Promise<T | null> {
  const started = DateTime.now();
  const durationMs = () => String(Math.round(DateTime.now().diff(started).toMillis()));
  try {
    const value = await read();
    void logWhenReady('info', 'galapagos.signup.read_completed', {
      operation,
      outcome: 'ok',
      failure: 'none',
      fallback: 'none',
      duration_ms: durationMs(),
    });
    return value;
  } catch (error) {
    void logWhenReady('warn', 'galapagos.signup.read_completed', {
      operation,
      outcome: 'failed',
      failure: failureOf(error),
      fallback,
      duration_ms: durationMs(),
    });
    return null;
  }
}
